import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AUDIT_ACTOR_KIND_LABEL, AUDIT_EVENT_LABEL, AuditEventType, PLATFORM_AUDIT_EVENTS } from '@suite/contracts';
import { Filters, createFilterQuery, flattenFilterRules, type FilterField, type FilterOperator, type FilterQuery } from '@xiltrion/ui/filters';
import { createDateFilterField, type FilterDateRuleValue } from '@xiltrion/ui/filters/filters-date-editor';
import { resolveFilterDate } from '@xiltrion/ui/filters/filters-date';
import { EstadoError, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@xiltrion/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import { apiGet } from '@/api/client';
import type { AuditRow, EntityListRow } from '@/api/types';

// Solo los operadores que el API sabe aplicar.
const IS: FilterOperator[] = [{ value: 'is', label: 'es', arity: 'one' }];
const ANY_OF: FilterOperator[] = [{ value: 'is_any_of', label: 'es alguno de', arity: 'many' }];
const CONTAINS: FilterOperator[] = [{ value: 'contains', label: 'contiene', arity: 'one' }];
const DATE_OPERATORS = ['between', 'is', 'is_on_or_after', 'is_on_or_before'];

const eventLabel = (e: string) => AUDIT_EVENT_LABEL[e as AuditEventType] ?? e;
const many = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
const one = (v: unknown) => (Array.isArray(v) ? (v[0] as string | undefined) : typeof v === 'string' ? v : undefined);
const startOfDay = (d: Date | null | undefined) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : undefined);
const nextDay = (d: Date | null | undefined) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1) : undefined);

interface AuditParams { event?: string[]; actorKind?: string[]; actor?: string; entity?: string; from?: string; to?: string }

/** Los chips de la barra como parámetros del API; los que aún no tienen valor no filtran. Las fechas son días locales. */
function toParams(query: FilterQuery): AuditParams {
  const p: AuditParams = {};
  for (const r of flattenFilterRules(query)) {
    if (!r.operator) continue;
    const field = r.path[0];
    if (field === 'evento' && many(r.value).length) p.event = many(r.value);
    else if (field === 'origen' && many(r.value).length) p.actorKind = many(r.value);
    else if (field === 'persona' && one(r.value)?.trim()) p.actor = one(r.value)!.trim();
    else if (field === 'entidad' && one(r.value)) p.entity = one(r.value);
    else if (field === 'fecha' && r.value) {
      const v = r.value as FilterDateRuleValue;
      let from: Date | undefined, to: Date | undefined;
      if (Array.isArray(v)) { from = startOfDay(resolveFilterDate(v[0])); to = nextDay(resolveFilterDate(v[1])); }
      else {
        const d = resolveFilterDate(v);
        if (r.operator !== 'is_on_or_before') from = startOfDay(d);
        if (r.operator !== 'is_on_or_after') to = nextDay(d);
      }
      p.from = from?.toISOString();
      p.to = to?.toISOString();
    }
  }
  return p;
}

/**
 * Auditoría con filtros (solo lectura, más reciente primero). Sin `entity` es la de toda la suite (Datalog › Auditoría)
 * y se filtra por entidad; con `entity`, la de esa entidad para su administrador, sin selector de entidad.
 */
export function AuditLog({ entity }: { entity?: string }) {
  const global = !entity;
  const [query, setQuery] = useState<FilterQuery>(() => createFilterQuery([]));
  const params = useMemo(() => toParams(query), [query]);
  const { data: entities = [] } = useQuery({ queryKey: ['entities'], queryFn: () => apiGet<EntityListRow[]>('/entities'), enabled: global });

  const fields = useMemo<FilterField[]>(() => [
    {
      id: 'evento', label: 'Evento', type: 'select', operators: ANY_OF, defaultOperator: 'is_any_of', placeholder: 'Buscar evento…', pinSelected: true, icon: <Icono nombre="mdi:lightning-bolt-outline" />,
      options: Object.values(AuditEventType).filter((e) => global || !PLATFORM_AUDIT_EVENTS.includes(e)).map((e) => ({ value: e, label: AUDIT_EVENT_LABEL[e] })).sort((a, b) => a.label.localeCompare(b.label, 'es')),
    },
    ...(global ? [{ id: 'entidad', label: 'Entidad', type: 'select' as const, operators: IS, placeholder: 'Buscar entidad…', icon: <Icono nombre="mdi:domain" />, options: entities.map((e) => ({ value: e.slug, label: e.name })) }] : []),
    { id: 'persona', label: 'Persona', type: 'text', operators: CONTAINS, defaultOperator: 'contains', placeholder: 'Correo…', icon: <Icono nombre="mdi:account-outline" /> },
    { id: 'origen', label: 'Origen', type: 'select', operators: ANY_OF, defaultOperator: 'is_any_of', searchable: false, icon: <Icono nombre="mdi:source-branch" />, options: Object.entries(AUDIT_ACTOR_KIND_LABEL).map(([value, label]) => ({ value, label })) },
    createDateFilterField('fecha', 'Fecha', { operators: DATE_OPERATORS, icon: <Icono nombre="mdi:calendar-range-outline" /> }),
  ], [global, entities]);

  const path = global ? '/admin/audit' : `/entities/${entity}/audit`;
  const { data, error, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['audit', path, params],
    queryFn: ({ pageParam }) => apiGet<{ items: AuditRow[]; nextCursor: string | null }>(path, { ...params, cursor: pageParam, limit: 100 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const [open, setOpen] = useState<AuditRow | null>(null);

  const columnas: ColumnaTabla<AuditRow>[] = [
    { key: 'when', titulo: 'Cuándo', valor: (r) => r.timestamp, ordenable: false, unaLinea: true, render: (r) => <span className="tnum text-xs">{new Date(r.timestamp).toLocaleString('es-CO')}</span> },
    { key: 'event', titulo: 'Evento', valor: (r) => eventLabel(r.event), ordenable: false, unaLinea: true, render: (r) => <Badge variant="secondary" title={r.event}>{eventLabel(r.event)}</Badge> },
    ...(global ? [{ key: 'entity', titulo: 'Entidad', valor: (r: AuditRow) => r.orgEntityName ?? '', ordenable: false, render: (r: AuditRow) => r.orgEntityName ?? <span className="text-muted-foreground">Suite</span> }] : []),
    { key: 'actor', titulo: 'Quién', valor: (r) => r.actorEmail ?? '', ordenable: false, render: (r) => <Actor row={r} /> },
    {
      key: 'detail', titulo: 'Detalle', valor: () => null, ordenable: false, clase: 'w-px',
      render: (r) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Ver detalle" onClick={(e) => { e.stopPropagation(); setOpen(r); }}><Icono nombre="mdi:eye-outline" /></Button>
          </TooltipTrigger>
          <TooltipContent>Ver detalle</TooltipContent>
        </Tooltip>
      ),
    },
  ];
  const filtering = Object.keys(params).length > 0;
  return (
    <div className="space-y-4">
      <Filters fields={fields} query={query} onQueryChange={setQuery} showClear size="sm" />
      {error ? <EstadoError error={error} /> : (
        <Tabla
          filas={rows}
          columnas={columnas}
          claveFila={(r) => r.id}
          onClicFila={setOpen}
          cargando={isLoading}
          masFilas={{ hayMas: !!hasNextPage, cargando: isFetchingNextPage, onCargar: () => void fetchNextPage() }}
          vacio={<EstadoVacio titulo={filtering ? 'Ningún evento con estos filtros' : 'Sin eventos'} />}
        />
      )}
      <AuditDetail row={open} onClose={() => setOpen(null)} />
    </div>
  );
}

const isEmpty = (v: unknown) => v == null || (typeof v === 'object' && Object.keys(v).length === 0);

/** Detalle de un evento en un panel lateral: quién, sobre qué, y el antes, el después y los metadatos completos. */
function AuditDetail({ row, onClose }: { row: AuditRow | null; onClose: () => void }) {
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        {row && (
          <>
            <SheetHeader>
              <SheetTitle>{eventLabel(row.event)}</SheetTitle>
              <SheetDescription className="tnum">{new Date(row.timestamp).toLocaleString('es-CO')}</SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-4">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Quién</dt><dd><Actor row={row} /></dd>
                <dt className="text-muted-foreground">Entidad</dt><dd>{row.orgEntityName ?? 'Suite'}</dd>
                {row.entityType && <><dt className="text-muted-foreground">Objeto</dt><dd className="break-all">{row.entityType}{row.entityId ? <span className="text-muted-foreground"> · {row.entityId}</span> : null}</dd></>}
                <dt className="text-muted-foreground">Código</dt><dd><code className="text-xs">{row.event}</code></dd>
              </dl>
              <Json titulo="Antes" value={row.before} />
              <Json titulo="Después" value={row.after} />
              <Json titulo="Metadatos" value={row.metadata} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Json({ titulo, value }: { titulo: string; value: unknown }) {
  if (isEmpty(value)) return null;
  return (
    <section className="space-y-1.5">
      <h3 className="text-sm font-medium">{titulo}</h3>
      <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(value, null, 2)}</pre>
    </section>
  );
}

/** Quién actuó: la persona (o el correo intentado en un ingreso fallido), si no el origen; y la app que llamó. */
function Actor({ row }: { row: AuditRow }) {
  const meta = row.metadata as { app?: string; email?: string };
  const who = row.actorEmail ?? meta.email ?? AUDIT_ACTOR_KIND_LABEL[row.actorKind as keyof typeof AUDIT_ACTOR_KIND_LABEL] ?? row.actorKind;
  return <span>{who}{meta.app ? <span className="text-muted-foreground"> · {meta.app}</span> : null}</span>;
}
